import type { Ton } from '../commun.js'
import {
  cheminAppareil, estPerime, fmtPct, fmtVal, niveauSeuil, tonMesure, trierAttention,
  type DocDomaine, type MesureGenerique, type NiveauMesure, type PointAttention, type ResumeAppareil, type Seuil, type SondeMesure,
} from '../reseau/reseau-model.js'

/**
 * SERVEUR — LE MODÈLE (fonctions pures, 21/10/2026).
 *
 * La vue `/v1/server` de Sonde porte l'hôte, les interfaces, les conteneurs
 * et, dans `generic`, les mesures par entité : `fs:<disque>`, `raid:<volume>`,
 * `disk:<baie>`, `idrac:<composant>`, `backup:serveur`, `npm_host:<site>`,
 * `docker:*`, `ups:*`. Ce module les range pour l'écran, avec deux règles :
 *   · un disque NON MONTÉ (événement SERVER_DISK_UNMOUNTED) ou dont la mesure
 *     est trop vieille est une donnée PÉRIMÉE, grise — jamais « 0 % » ;
 *   · une santé non relevée (null) est grise, jamais verte.
 */

/* ═══════════════ Formes de Sonde ═══════════════ */

export interface Hote {
  host: string
  cpu_pct: number | null
  load1: number | null
  load5: number | null
  load15: number | null
  mem_avail_pct: number | null
  mem_used_mb: number | null
  swap_pct: number | null
  psi_cpu_some: number | null
  psi_mem_some: number | null
  psi_io_some: number | null
  temp_cpu_c: number | null
  temp_max_c: number | null
  clock_offset_s: number | null
  udp_rcvbuf_err_1h: number | null
  procs: number | null
  levels: Record<string, NiveauMesure>
  at: string | null
  data_age_s: number | null
}

export interface InterfaceHote {
  iface: string
  up: boolean | null
  speed_mbps: number | null
  rx_bps: number | null
  tx_bps: number | null
  rx_err_1h: number | null
  tx_err_1h: number | null
  rx_drop_1h: number | null
  tx_drop_1h: number | null
  carrier_changes_24h: number | null
  bond_active: boolean | null
  at: string | null
  data_age_s: number | null
}

export interface Conteneur {
  name: string
  uid: string | null
  running: boolean | null
  health: number | null
  health_label: string | null
  cpu_pct: number | null
  mem_mb: number | null
  restarts_1h: number | null
  oom_24h: number | null
  stopped_on_purpose: boolean
  display_state: string | null
  at: string | null
  data_age_s: number | null
}

export interface DocServeur extends DocDomaine {
  host: Hote | null
  interfaces: InterfaceHote[]
  containers: Conteneur[]
  devices: ResumeAppareil[]
  probes: SondeMesure[]
  generic: MesureGenerique[]
}

export type NiveauFragilite = 'faible' | 'moyenne' | 'élevée' | null

export interface DocFragilite {
  computed_at: string
  age_s: number | null
  score: number | null
  level: NiveauFragilite
  headline_fr: string
  components: { key: string; label: string; score: number | null; level: NiveauFragilite; factors: { points: number; reason_fr: string }[]; trend_fr: string | null }[]
  devices: { uid: string; name: string; kind: string; zone: string | null; score: number; outages_7d: number; transitions_24h: number; flapping_events_7d: number; reasons_fr: string[] }[]
}

export interface Prevision {
  key: string
  label: string
  entity: string
  unit: string
  current: number | null
  days: number
  slope_per_day: number | null
  confidence: 'insuffisante' | 'faible' | 'moyenne' | 'bonne'
  state: 'ok' | 'warn' | 'crit' | 'unknown'
  eta_warn: string | null
  eta_crit: string | null
  within_horizon: boolean
  message_fr: string
}

export interface DocPrevisions { computed_at: string; age_s: number | null; enabled: boolean; horizon_days: number; series: Prevision[] }

export interface Evenement {
  id: number
  at: string
  kind: string
  kind_label: string
  severity: 'info' | 'warning' | 'error' | 'critical'
  uid: string | null
  entity_key: string | null
  payload: Record<string, unknown>
}

export interface DocEvenements { items: Evenement[]; next_cursor: string | null }

const objet = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x)

export const estDocServeur = (x: unknown): x is DocServeur =>
  objet(x) && objet(x['collector']) && Array.isArray(x['sources']) && (x['host'] === null || objet(x['host']))
  && Array.isArray(x['containers']) && Array.isArray(x['interfaces']) && Array.isArray(x['generic']) && Array.isArray(x['probes'])
export const estDocFragilite = (x: unknown): x is DocFragilite => objet(x) && typeof x['headline_fr'] === 'string' && Array.isArray(x['components']) && Array.isArray(x['devices'])
export const estDocPrevisions = (x: unknown): x is DocPrevisions => objet(x) && typeof x['enabled'] === 'boolean' && Array.isArray(x['series'])
export const estDocEvenements = (x: unknown): x is DocEvenements => objet(x) && Array.isArray(x['items'])

/* ═══════════════ Mesures génériques ═══════════════ */

export type Index = Map<string, Map<string, MesureGenerique>>

export function indexer(g: MesureGenerique[]): Index {
  const out: Index = new Map()
  for (const m of g) {
    let e = out.get(m.entity_key)
    if (!e) { e = new Map(); out.set(m.entity_key, e) }
    e.set(m.metric, m)
  }
  return out
}

export const valeur = (idx: Index, entite: string, metrique: string): number | null => idx.get(entite)?.get(metrique)?.value ?? null
export const mesure = (idx: Index, entite: string, metrique: string): MesureGenerique | null => idx.get(entite)?.get(metrique) ?? null
export const entites = (idx: Index, prefixe: string): string[] => [...idx.keys()].filter((k) => k.startsWith(prefixe)).sort((a, b) => a.localeCompare(b, 'fr', { numeric: true }))

/** Santé iDRAC / RAID / disque : 0 OK, 1 avertissement, 2 critique ; null = non relevée (gris). */
export function etatSante(v: number | null | undefined): { ton: Ton; libelle: string } {
  if (v === 0) return { ton: 'ok', libelle: 'OK' }
  if (v === 1) return { ton: 'warn', libelle: 'Avertissement' }
  if (v === 2) return { ton: 'err', libelle: 'Critique' }
  return { ton: 'gris', libelle: 'Non relevé' }
}

/* ═══════════════ Disques ═══════════════ */

/** Âge au-delà duquel le remplissage d'un disque (relevé chaque minute) est périmé. */
export const PERIME_DISQUE_S = 300

const LIBELLE_DISQUE: Record<string, string> = {
  systeme: 'Système (RAID 1)',
  donnees: 'Données Nextcloud',
  ssd: 'SSD (sauvegardes, base de Sonde)',
}

export const libelleDisque = (cle: string): string => LIBELLE_DISQUE[cle] ?? cle.charAt(0).toUpperCase() + cle.slice(1)

export interface Disque {
  cle: string
  libelle: string
  /** null dès que la donnée est périmée ou le disque démonté : jamais « 0 % ». */
  usedPct: number | null
  libreGo: number | null
  tailleGo: number | null
  inodesPct: number | null
  /** Dernière mesure connue, pour la dire « périmée » sans la faire passer pour actuelle. */
  dernierPct: number | null
  age: number | null
  at: string | null
  demonte: boolean
  perime: boolean
  message: string | null
  niveau: NiveauMesure
  ton: Ton
  etat: string
}

/**
 * Disques du serveur : mesures `fs:<clé>` + derniers événements de montage.
 * Le dernier événement d'un disque fait foi (les événements arrivent du plus
 * récent au plus ancien) ; un disque démonté dès le démarrage, sans aucune
 * mesure, apparaît quand même.
 */
export function disques(idx: Index, evenements: Evenement[] | null, seuil: Seuil | null): Disque[] {
  const dernier = new Map<string, Evenement>()
  for (const e of [...(evenements ?? [])].sort((a, b) => b.at.localeCompare(a.at) || b.id - a.id)) {
    if (!e.entity_key?.startsWith('fs:') || (e.kind !== 'SERVER_DISK_UNMOUNTED' && e.kind !== 'SERVER_DISK_MOUNTED')) continue
    if (!dernier.has(e.entity_key)) dernier.set(e.entity_key, e)
  }
  const cles = [...new Set([...entites(idx, 'fs:'), ...dernier.keys()])]
  return cles.map((entite) => {
    const cle = entite.slice(3)
    const m = mesure(idx, entite, 'serveur.fs_used_pct')
    const ev = dernier.get(entite)
    const demonte = ev?.kind === 'SERVER_DISK_UNMOUNTED'
    const age = m?.data_age_s ?? null
    const perime = demonte || estPerime(age, PERIME_DISQUE_S)
    const niveau = perime ? null : niveauSeuil(m?.value ?? null, seuil)
    const message = demonte && typeof ev?.payload['message_fr'] === 'string' ? ev.payload['message_fr'] : null
    return {
      cle,
      libelle: libelleDisque(cle),
      usedPct: perime ? null : m?.value ?? null,
      libreGo: perime ? null : valeur(idx, entite, 'serveur.fs_free_gb'),
      tailleGo: valeur(idx, entite, 'serveur.fs_size_gb'),
      inodesPct: perime ? null : valeur(idx, entite, 'serveur.fs_inodes_used_pct'),
      dernierPct: m?.value ?? null,
      age,
      at: m?.at ?? null,
      demonte,
      perime,
      message,
      niveau,
      ton: perime ? 'gris' : tonMesure(niveau),
      etat: demonte ? 'Non monté — donnée périmée'
        : perime ? 'Donnée périmée'
          : niveau === 'critical' ? 'Presque plein' : niveau === 'warning' ? 'À surveiller' : niveau === 'ok' ? 'Normal' : 'Inconnu',
    }
  }).sort((a, b) => a.cle.localeCompare(b.cle, 'fr'))
}

/* ═══════════════ RAID, disques physiques, iDRAC ═══════════════ */

export interface Volume { id: string; libelle: string; sante: number | null; degrade: number | null; reconstruction: number | null; age: number | null }
export interface DisquePhysique { id: string; libelle: string; sante: number | null; pannePredite: number | null; endurance: number | null; age: number | null }

export function volumes(idx: Index): Volume[] {
  return entites(idx, 'raid:').filter((e) => e !== 'raid:serveur').map((e) => {
    const id = e.slice(5)
    const n = /disk\.virtual\.(\d+)/i.exec(id)
    return {
      id,
      libelle: n ? `Volume virtuel ${n[1]}` : id,
      sante: valeur(idx, e, 'serveur.raid_volume_health'),
      degrade: valeur(idx, e, 'serveur.raid_volume_degraded'),
      reconstruction: valeur(idx, e, 'serveur.raid_rebuild_pct'),
      age: mesure(idx, e, 'serveur.raid_volume_health')?.data_age_s ?? null,
    }
  })
}

export function disquesPhysiques(idx: Index): DisquePhysique[] {
  return entites(idx, 'disk:').map((e) => {
    const id = e.slice(5)
    const n = /disk\.bay\.(\d+)/i.exec(id)
    return {
      id,
      libelle: n ? `Baie ${n[1]}` : id,
      sante: valeur(idx, e, 'serveur.disk_health'),
      pannePredite: valeur(idx, e, 'serveur.disk_failure_predicted'),
      endurance: valeur(idx, e, 'serveur.disk_life_left_pct'),
      age: mesure(idx, e, 'serveur.disk_health')?.data_age_s ?? null,
    }
  })
}

const LIBELLE_IDRAC: Record<string, string> = {
  'rollup.battery': 'Piles', 'rollup.current': 'Courant', 'rollup.fan': 'Ventilateurs', 'rollup.idsdm': 'Module IDSDM',
  'rollup.intrusion': 'Ouverture du châssis', 'rollup.licensing': 'Licences', 'rollup.ps': 'Alimentations', 'rollup.sdcard': 'Carte SD',
  'rollup.sel': 'Journal matériel', 'rollup.storage': 'Stockage', 'rollup.temp': 'Températures', 'rollup.volt': 'Tensions',
  cpu: 'Processeurs', memory: 'Mémoire', system: 'Système', idrac: 'iDRAC',
}

export function libelleIdrac(cle: string): string {
  if (LIBELLE_IDRAC[cle]) return LIBELLE_IDRAC[cle]
  let m = /^fan\.(\d+)$/.exec(cle)
  if (m) return `Ventilateur ${Number(m[1]) + 1}`
  m = /^psu\.(\d+)$/.exec(cle)
  if (m) return `Alimentation ${Number(m[1]) + 1}`
  m = /^temp\.(\d+)$/.exec(cle)
  if (m) return `Sonde de température ${Number(m[1]) + 1}`
  if (cle.startsWith('controller.raid')) return 'Contrôleur RAID'
  if (cle.startsWith('controller.ahci')) return 'Contrôleur AHCI'
  if (cle.startsWith('battery.raid')) return 'Batterie du contrôleur RAID'
  return cle
}

export interface ComposantIdrac { cle: string; libelle: string; sante: number | null; groupe: 'synthese' | 'composant' }

/** Santé des composants iDRAC : synthèses (`rollup.*`) d'abord, puis le détail. */
export function composantsIdrac(idx: Index): ComposantIdrac[] {
  return entites(idx, 'idrac:')
    .filter((e) => idx.get(e)?.has('serveur.idrac_health'))
    .map((e) => {
      const cle = e.slice(6)
      return { cle, libelle: libelleIdrac(cle), sante: valeur(idx, e, 'serveur.idrac_health'), groupe: cle.startsWith('rollup.') ? 'synthese' as const : 'composant' as const }
    })
}

/** Capteurs iDRAC chiffrés d'une famille (`idrac:fan:N`, `idrac:temp:N`, `idrac:psu:N`). */
export function capteurs(idx: Index, famille: 'fan' | 'temp' | 'psu', metrique: string): { n: number; valeur: number | null }[] {
  return entites(idx, `idrac:${famille}:`)
    .map((e) => ({ n: Number(e.split(':')[2]) + 1, valeur: valeur(idx, e, metrique) }))
    .sort((a, b) => a.n - b.n)
}

/* ═══════════════ Sauvegarde, certificats, Docker ═══════════════ */

export interface Certificat { hote: string; jours: number | null; niveau: NiveauMesure; age: number | null }

export function certificats(idx: Index, seuil: Seuil | null): Certificat[] {
  return entites(idx, 'npm_host:')
    .filter((e) => idx.get(e)?.has('services.cert_days_left'))
    .map((e) => {
      const m = mesure(idx, e, 'services.cert_days_left')
      const jours = m?.value ?? null
      return { hote: e.slice('npm_host:'.length), jours, niveau: estPerime(m?.data_age_s, 3 * 3600) ? null : niveauSeuil(jours, seuil), age: m?.data_age_s ?? null }
    })
    .sort((a, b) => (a.jours ?? Infinity) - (b.jours ?? Infinity))
}

/** Un booléen relevé (1/0) : null = non relevé (gris). */
export const oui = (v: number | null): boolean | null => (v === null ? null : v >= 1)

/** État d'un conteneur : l'état de Sonde prime ; « arrêté exprès » n'est jamais une panne. */
export function etatConteneur(c: Conteneur, seuilRedemarrages: Seuil | null): { ton: Ton; libelle: string } {
  if (c.stopped_on_purpose && c.running !== true) return { ton: 'off', libelle: 'Arrêté exprès' }
  if (estPerime(c.data_age_s)) return { ton: 'gris', libelle: 'Donnée périmée' }
  const redem = niveauSeuil(c.restarts_1h, seuilRedemarrages)
  if (c.display_state === 'OFFLINE') return { ton: 'err', libelle: 'Hors ligne' }
  if (c.display_state === 'DEGRADED') return { ton: 'warn', libelle: 'Dégradé' }
  if (c.display_state === 'STALE' || c.display_state === 'UNKNOWN' || c.display_state === 'UNAVAILABLE') {
    return { ton: 'gris', libelle: c.running === false ? 'Arrêté (sans état récent)' : 'Inconnu' }
  }
  if (c.display_state === 'MAINTENANCE') return { ton: 'maint', libelle: 'En maintenance' }
  if (c.display_state === 'OFF_EXPECTED') return { ton: 'off', libelle: 'Éteint comme prévu' }
  if (c.running === false) return { ton: 'err', libelle: 'Arrêté' }
  if (c.health === 3) return { ton: 'err', libelle: 'Malade' }
  if (redem === 'critical') return { ton: 'err', libelle: `${c.restarts_1h} redémarrages en 1 h` }
  if (redem === 'warning' || (c.oom_24h ?? 0) > 0) return { ton: 'warn', libelle: (c.oom_24h ?? 0) > 0 ? 'Mémoire saturée (24 h)' : `${c.restarts_1h} redémarrages en 1 h` }
  if (c.health === 1) return { ton: 'warn', libelle: 'Démarrage' }
  if (c.running === true) return { ton: 'ok', libelle: c.health === 2 ? 'Sain' : 'En marche' }
  return { ton: 'gris', libelle: 'Inconnu' }
}

/* ═══════════════ Fragilité et prévisions ═══════════════ */

export const tonFragilite = (l: NiveauFragilite): Ton => l === 'élevée' ? 'err' : l === 'moyenne' ? 'warn' : l === 'faible' ? 'ok' : 'gris'
export const tonPrevision = (s: Prevision['state']): Ton => s === 'crit' ? 'err' : s === 'warn' ? 'warn' : s === 'ok' ? 'ok' : 'gris'

/** Jours entiers d'ici une date (arrondi au-dessus) ; null sans date. */
export function joursAvant(iso: string | null, maintenant = Date.now()): number | null {
  if (!iso) return null
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return null
  return Math.max(0, Math.ceil((t - maintenant) / 86_400_000))
}

export const GROUPE_PREVISION: Record<string, string> = {
  server_disk: 'Disques du serveur',
  cert_days: 'Certificats',
  gateway_mem: 'Mémoire de la passerelle',
  ups_runtime: 'Autonomie de l’onduleur',
  nvr_free_ram: 'Mémoire des NVR',
  nvr_retention: 'Rétention des NVR',
  protect_retention: 'Rétention de Protect',
}

/** « plein dans 12 j » pour un disque, sinon la phrase de Sonde. */
export function echeance(p: Prevision, maintenant = Date.now()): string | null {
  const crit = joursAvant(p.eta_crit, maintenant)
  const warn = joursAvant(p.eta_warn, maintenant)
  const quoi = p.key === 'server_disk' ? 'plein' : p.key === 'cert_days' ? 'expiration proche' : 'seuil critique'
  if (crit !== null) return `${quoi} dans ${crit} j`
  if (warn !== null) return `seuil d’alerte dans ${warn} j`
  return null
}

/* ═══════════════ Ce qui demande attention ═══════════════ */

export function attentionServeur(d: {
  doc: DocServeur | null
  idx: Index
  disques: Disque[]
  certificats: Certificat[]
  previsions: DocPrevisions | null
}): PointAttention[] {
  const out: PointAttention[] = []
  const h = d.doc?.host ?? null
  if (h && !estPerime(h.data_age_s)) {
    const libelles: Record<string, [string, string]> = {
      mem_avail_pct: ['Mémoire disponible', fmtPct(h.mem_avail_pct, 1)],
      swap_pct: ['Mémoire d’échange', fmtPct(h.swap_pct, 1)],
      temp_max_c: ['Température du processeur', fmtVal(h.temp_max_c ?? h.temp_cpu_c, '°C')],
      psi_io_some: ['Attente des disques', fmtPct(h.psi_io_some, 1)],
      clock_offset_s: ['Dérive d’horloge', fmtVal(h.clock_offset_s, 's', 2)],
    }
    for (const [k, l] of Object.entries(h.levels)) {
      if (l !== 'warning' && l !== 'critical') continue
      const [nom, v] = libelles[k] ?? [k, '']
      out.push({ ton: tonMesure(l), texte: `${nom} : ${v}` })
    }
  }
  for (const x of d.disques) {
    if (x.demonte) out.push({ ton: 'err', texte: `Disque « ${x.libelle} » non monté sur le serveur` })
    else if (x.niveau === 'warning' || x.niveau === 'critical') out.push({ ton: tonMesure(x.niveau), texte: `Disque « ${x.libelle} » rempli à ${fmtPct(x.usedPct, 1)}` })
  }
  for (const v of volumes(d.idx)) {
    if (v.degrade !== null && v.degrade >= 1) out.push({ ton: 'err', texte: `${v.libelle} : RAID dégradé${v.reconstruction !== null ? `, reconstruction ${fmtPct(v.reconstruction)}` : ''}` })
    else if (v.sante === 1 || v.sante === 2) out.push({ ton: etatSante(v.sante).ton, texte: `${v.libelle} : santé ${etatSante(v.sante).libelle.toLowerCase()}` })
  }
  for (const p of disquesPhysiques(d.idx)) {
    if (p.pannePredite !== null && p.pannePredite >= 1) out.push({ ton: 'err', texte: `Disque ${p.libelle} : panne annoncée (SMART)` })
    else if (p.sante === 1 || p.sante === 2) out.push({ ton: etatSante(p.sante).ton, texte: `Disque ${p.libelle} : santé ${etatSante(p.sante).libelle.toLowerCase()}` })
  }
  for (const c of composantsIdrac(d.idx)) {
    if (c.groupe === 'synthese' && (c.sante === 1 || c.sante === 2)) out.push({ ton: etatSante(c.sante).ton, texte: `iDRAC — ${c.libelle} : ${etatSante(c.sante).libelle.toLowerCase()}` })
  }
  const selCrit = valeur(d.idx, 'idrac:sel', 'serveur.idrac_sel_critical')
  if (selCrit !== null && selCrit > 0) out.push({ ton: 'warn', texte: `${selCrit} entrée${selCrit > 1 ? 's' : ''} critique${selCrit > 1 ? 's' : ''} au journal matériel (iDRAC)` })
  const b = 'backup:serveur'
  if (oui(valeur(d.idx, b, 'serveur.backup_last_ok')) === false) out.push({ ton: 'err', texte: 'Dernière sauvegarde du serveur en échec' })
  if (oui(valeur(d.idx, b, 'serveur.backup_integrity_ok')) === false) out.push({ ton: 'err', texte: 'Dernière sauvegarde du serveur incomplète ou illisible' })
  const trous = valeur(d.idx, b, 'serveur.backup_daily_gaps')
  if (trous !== null && trous > 0) out.push({ ton: 'warn', texte: `${trous} jour${trous > 1 ? 's' : ''} sans sauvegarde dans la rétention` })
  for (const c of d.certificats) {
    if (c.niveau === 'warning' || c.niveau === 'critical') out.push({ ton: tonMesure(c.niveau), texte: `Certificat ${c.hote} : expire dans ${fmtVal(c.jours, 'j')}` })
  }
  const seuilRedem = d.doc?.thresholds?.['thresholds.services.restarts_per_hour'] ?? null
  for (const c of d.doc?.containers ?? []) {
    const e = etatConteneur(c, seuilRedem)
    if (e.ton === 'err' || e.ton === 'warn') out.push({ ton: e.ton, texte: `Conteneur ${c.name} : ${e.libelle.toLowerCase()}`, ...(c.uid ? { href: cheminAppareil(c.uid) } : {}) })
  }
  const collisions = valeur(d.idx, 'docker:routes', 'serveur.docker_vlan_collisions')
  if (collisions !== null && collisions > 0) out.push({ ton: 'err', texte: `${collisions} réseau${collisions > 1 ? 'x' : ''} Docker couvre${collisions > 1 ? 'nt' : ''} un réseau du club` })
  if (valeur(d.idx, 'docker:pool', 'serveur.docker_pool_free') === 0) out.push({ ton: 'warn', texte: 'Plus aucun réseau Docker libre dans la réserve 172.17–172.31' })
  if (valeur(d.idx, 'iface:bond0', 'serveur.bond_on_primary') === 0) out.push({ ton: 'warn', texte: 'Le lien réseau du serveur est passé sur son secours' })
  for (const p of d.doc?.probes ?? []) {
    if (estPerime(p.data_age_s) || (p.level !== 'warning' && p.level !== 'critical')) continue
    out.push({ ton: tonMesure(p.level), texte: `${p.probe_key.replace(/^endpoint:/, 'Service ')} : ${p.ok === false ? 'sans réponse' : fmtVal(p.latency_ms, 'ms')}` })
  }
  for (const s of d.previsions?.series ?? []) {
    if ((s.state === 'warn' || s.state === 'crit') && s.within_horizon && s.key !== 'cert_days') {
      out.push({ ton: tonPrevision(s.state), texte: `${s.label} : ${echeance(s) ?? s.message_fr}` })
    }
  }
  return trierAttention(out)
}
